import { useCallback, useEffect, useMemo, useState } from 'react';
import { useStoreState, useStoreActions } from '../../../store';
import { SimulationUploadsTable } from './SimulationUploadsTable';
import { Form, Slider, Space, Alert, message, Typography } from 'antd';
import { BASE_DETAILS_WORKFLOW_KEY } from './SimulationBaseDetailsStep';
import { SHIPMENT_SELECTION_KEY, UPLOAD_DATASET_MODE } from './SimulationShipmentSelectStep';
import DatePicker from '../../dateFnsComponents/DatePicker';
import {
  endOfDay,
  startOfDay,
  format,
  subDays,
  addDays,
  differenceInCalendarDays,
  parse,
  subHours,
  addHours,
} from 'date-fns';
import type { Simulation, SimulationSetupData, UploadedShipmentData } from '../../../store/simulation/types';
import { useSearchParams } from 'react-router-dom-v5-compat';
import { SimulationWorkflowCard } from './SimulationWorkflowCard';
import { SIMULATION_ID_SEARCH_PARAM } from '../../../pages/Simulation/SimulationLandingPage';
import { useWatch } from 'antd/lib/form/Form';
import { useShipmentSizeAlert } from './ShipmentSizeAlert';
import {
  type UnifiedSimulationUpload,
  getFileUploadId,
  enrichWithFileUploadDetail,
  needsFileUploadDetail,
  toPersistableSelection,
  toSimulationSourceLocation,
} from './uploadAdapter';
import { simulationService } from '~/services';
import { useQueryClient } from '@tanstack/react-query';
import { queryCreator } from '~/hooks/useQueryCreator';
import { useSession } from '~/hooks/useSession';
import { useSimulationVersion } from '~/hooks/simulation/useSimulationVersion';
import { useUpdateSimulation } from '~/hooks/simulation/useSimulationSetupMutations';
import { useSimulationShipmentCount } from '~/hooks/simulation/useSimulationShipmentCount';
const { RangePicker } = DatePicker;
export const SimulationUploadDateValidator = async (ruleObject: any, value: any) => {
  if (value) {
    const dayDiff = Math.abs(differenceInCalendarDays(value[0], value[1]));
    if (dayDiff >= 32) {
      throw new Error('Selected date range exceeds one month');
    }
  }
};
// The batcher create batches based on UTC data
// if you have a shpment after 5 pm or before 7 am west coast
// local time on the earliest or latest date it might get excluded from the batch
// this hack might cause the creation of unnecessary empty batches
// but at least the shipments will be included in the batches
// plus there are already empty batches with our current strategy
// REMOVE IF BATCHING STRATEGY IS CHANGED - RE 10-08-2025
const hackMinDate = (date: Date) => {
  return subHours(date, 12);
};
const hackMaxDate = (date: Date) => {
  return addHours(date, 12);
};
export const UsePreviousHistoricalShipments = () => {
  const queryClient = useQueryClient();
  const { sessionPartnerUrl } = useSession();
  const updateSimulationStep = useStoreActions((actions) => actions.simulation.setActiveNavigationStep);
  const simulationStep = useStoreState((state) => state.simulation.activeNavigationStep);
  const simulationSetupData = useStoreState((state) => state.simulation.simulationSetupData);
  const setSimulationSetupData = useStoreActions((actions) => actions.simulation.setSimulationSetupData);
  const { mutateAsync: updateSimulation } = useUpdateSimulation();
  const [selectedFiles, setSelectedFiles] = useState<UnifiedSimulationUpload[]>([]);
  const [form] = Form.useForm();
  const [dateRangeState, setDateRangeState] = useState<any>([]);
  const [datePickerTouched, setDatePickerTouched] = useState<boolean>(false);
  const [query] = useSearchParams();
  const simulationId = query.get(SIMULATION_ID_SEARCH_PARAM);
  const { version: serviceVersion } = useSimulationVersion(simulationId);
  const samplingPercentage = useWatch('samplingPercentage', form);

  useEffect(() => {
    if (simulationId && simulationSetupData[SHIPMENT_SELECTION_KEY]) {
      const { end, start, samplingPercentage, selectedFiles } = simulationSetupData[
        SHIPMENT_SELECTION_KEY
      ] as UploadedShipmentData;
      form.setFieldsValue({
        dateRange: [parse(start, 'yyyy-MM-dd', new Date()), parse(end, 'yyyy-MM-dd', new Date())],
        samplingPercentage: Number(samplingPercentage ?? 0),
      });
      setSelectedFiles(selectedFiles);
    }
  }, [simulationId, simulationSetupData, form]);

  const latestSelectedShipmentDate =
    selectedFiles?.length > 0
      ? selectedFiles.reduce((prev: Date, val) => {
          if (!val?.details?.maxShipDate) {
            return prev;
          }

          if (!prev || new Date(val.details.maxShipDate) > new Date(prev)) {
            return new Date(val.details.maxShipDate);
          }

          return prev;
        }, new Date(0))
      : null;

  const earliestSelectedShipmentDate =
    selectedFiles?.length > 0
      ? selectedFiles.reduce((prev: Date, val) => {
          if (!val.details?.minShipDate) {
            return prev;
          }

          if (!prev || new Date(val.details.minShipDate) < new Date(prev)) {
            return new Date(val.details.minShipDate);
          }
          return prev;
        }, new Date())
      : null;

  useEffect(() => {
    if (!datePickerTouched && earliestSelectedShipmentDate && latestSelectedShipmentDate) {
      form.setFieldValue('dateRange', [
        hackMinDate(earliestSelectedShipmentDate),
        hackMaxDate(latestSelectedShipmentDate),
      ]);
    }
  }, [form, datePickerTouched, earliestSelectedShipmentDate, latestSelectedShipmentDate]);
  const selectedShipmentCount = useMemo(() => {
    return (
      selectedFiles?.reduce((prev, val) => {
        return (val?.details?.shipmentCount ?? 0) + prev;
      }, 0) ?? 0
    );
  }, [selectedFiles]);

  const isV2 = serviceVersion === 'v2';
  // v2 counts the EXACT in-window rows server-side (v1's file totals overstate a date-scoped run).
  const uploadIds = useMemo(
    () => selectedFiles.map(getFileUploadId).filter((id): id is string => !!id),
    [selectedFiles],
  );
  const dateRange = useWatch('dateRange', form);
  const [rangeStart, rangeEnd] = Array.isArray(dateRange) ? dateRange : [];
  const bothDates = !!rangeStart && !!rangeEnd;
  const {
    count: inWindowCount,
    isLoading: countLoading,
    errorMessage: countError,
  } = useSimulationShipmentCount({
    version: serviceVersion,
    uploadIds,
    startDate: bothDates ? format(startOfDay(rangeStart), 'yyyy-MM-dd') : undefined,
    endDate: bothDates ? format(endOfDay(rangeEnd), 'yyyy-MM-dd') : undefined,
  });

  const effectiveSampling = samplingPercentage ?? 100;
  const shipmentsToSimulate = isV2
    ? Math.floor((inWindowCount ?? 0) * (effectiveSampling / 100))
    : Math.floor(selectedShipmentCount * (samplingPercentage / 100));
  const { exceededMaxShipments, ShipmentSizeAlert } = useShipmentSizeAlert({ totalShipments: shipmentsToSimulate });

  // v2: block until a count resolves — an undefined count (loading / 400 / no upload ids) would
  // otherwise read as 0 and slip past the size gate.
  const saveDisabled = selectedFiles?.length < 1 || exceededMaxShipments || (isV2 && inWindowCount === undefined);

  const shipmentsToSimulateExtra =
    isV2 && countError ? (
      <Typography.Text type="warning">{countError}</Typography.Text>
    ) : isV2 && countLoading ? (
      'Calculating shipments to simulate…'
    ) : (
      `${shipmentsToSimulate.toLocaleString()} shipments to simulate`
    );

  const handleSelect = useCallback(
    async (files: UnifiedSimulationUpload[]) => {
      try {
        const enriched = await Promise.all(
          files.map(async (file) => {
            if (!needsFileUploadDetail(file)) return file;
            const detail = await queryClient.fetchQuery(
              queryCreator(sessionPartnerUrl, simulationService.fileUploads.get, {
                args: [getFileUploadId(file) as string],
              }),
            );
            return enrichWithFileUploadDetail(file, detail);
          }),
        );
        setSelectedFiles(enriched);
      } catch {
        message.error('Unable to select file, please try again');
      }
    },
    [queryClient, sessionPartnerUrl],
  );
  const handleSave = async () => {
    if (simulationId) {
      form
        .validateFields()
        .then(async () => {
          const { dateRange, samplingPercentage } = form.getFieldsValue();
          const [start, end] = dateRange;
          const formattedStart = format(startOfDay(start), 'yyyy-MM-dd');
          const formattedEnd = format(endOfDay(end), 'yyyy-MM-dd');
          const simulationData = simulationSetupData[BASE_DETAILS_WORKFLOW_KEY] as Simulation;
          const modifiedSetupData: SimulationSetupData = {
            ...simulationSetupData,
            [SHIPMENT_SELECTION_KEY]: {
              mode: UPLOAD_DATASET_MODE,
              // Persist the light selection; the mapping step re-derives the breakdown on demand.
              selectedFiles: toPersistableSelection(selectedFiles, serviceVersion),
              start: formattedStart,
              end: formattedEnd,
              samplingPercentage,
            },
          };
          setSimulationSetupData(modifiedSetupData);
          await updateSimulation({
            ...simulationData,
            simulationId,
            includesHistoricalSelection: false,
            samplingPercentage,
            historicalShipmentS3Locations:
              selectedFiles?.map((f) => toSimulationSourceLocation(f, serviceVersion)) ?? [],
            simulationSourceStartDate: format(startOfDay(start), 'yyyy-MM-dd'),
            simulationSourceEndDate: format(endOfDay(end), 'yyyy-MM-dd'),
            simulationSetupData: modifiedSetupData,
          });
          updateSimulationStep(simulationStep + 1);
        })
        .catch((err) => {
          // the error is often blocked by the table size
          window.scrollTo(0, 0);
          console.log('err', err);
        });
    }
  };
  const disabledDate = (current: any) => {
    // 32 gives us TZ wiggle room for a month idk
    // maybe unnecessary
    if (dateRangeState && !!dateRangeState[1] && subDays(dateRangeState[1], 32) > current) {
      return true;
    }
    if (dateRangeState && !!dateRangeState[0] && addDays(dateRangeState[0], 32) < current) {
      return true;
    }
    return false;
    // return current > moment() || current < moment().subtract(3, 'day');
  };
  return (
    <SimulationWorkflowCard onSave={handleSave} simulationId={simulationId} disabled={saveDisabled}>
      <Space direction="vertical" size={10} style={{ width: '100%' }}>
        <Alert
          message="Use Previous Simulation Dataset"
          description="Select a previously uploaded simulation dataset for this simulation."
          type="info"
          showIcon
        />
        <ShipmentSizeAlert />
      </Space>
      <Form form={form} style={{ marginTop: 10 }}>
        <Space size={100}>
          <Form.Item
            required={true}
            name={'dateRange'}
            rules={[
              { required: true },
              ...(serviceVersion === 'v2' ? [] : [{ validator: SimulationUploadDateValidator }]),
            ]}
            tooltip={
              'For this simulation we will only run shipments that fall within this date range, even if the files include shipments that fall outside of this range'
            }
            label={'Select a date range for this simulation'}
          >
            <RangePicker
              onCalendarChange={(dates) => {
                setDatePickerTouched(true);
                setDateRangeState(dates);
              }}
              disabledDate={serviceVersion === 'v2' ? undefined : disabledDate}
              showTime={false}
            />
          </Form.Item>
          <Form.Item
            initialValue={100}
            required={true}
            style={{ marginBottom: 0 }}
            name="samplingPercentage"
            extra={shipmentsToSimulateExtra}
            tooltip={
              'Use the sampling percentage to reduce the number of shipments to run for your simulation. This will help you get your results faster'
            }
            label="Shipment Sampling Percentage"
          >
            <Slider tooltip={{ open: true, placement: 'right' }} defaultValue={100} min={1} style={{ width: 200 }} />
          </Form.Item>
        </Space>
      </Form>
      <SimulationUploadsTable
        version={serviceVersion}
        selectedKeys={selectedFiles?.map((val) => val.s3Key) ?? []}
        allowNonHistorical={simulationSetupData[BASE_DETAILS_WORKFLOW_KEY]?.includesHistoricalSelection !== true}
        onSelect={(val: UnifiedSimulationUpload[]) => {
          handleSelect(val);
        }}
      />
    </SimulationWorkflowCard>
  );
};
